import { useMemo } from "react";
import { STUDENT_LIST } from "@/api/queries/studentList";
import { StudentListQuery } from "@/__generated__/graphql";
import { useQuery } from "@apollo/client";
import { FilterContext } from "@/types/FilterContext";
import { StudentOrdering } from "@/types/StudentOrdering";
import { useOrg } from "@/hooks/useOrg";
import { processStudent } from "@/util/studentUtils";
import { FilterState } from "@/components/FilterSidebar/FilterState";
import { PaginationVariables, PaginatedResults } from "@/util/pagination";
import { Student } from "@/types/Student";
import { useOrgPermissions } from "./useOrgPermissions";
import { PartnerFeatureEnum, YesNoEnum } from "@/__generated__/types";

export function useFetchStudents({
  paginationVariables,
  context,
  ordering,
  filterState = new FilterState({}),
  skip = false,
}: {
  paginationVariables?: PaginationVariables | undefined;
  context?: FilterContext;
  filterState?: FilterState;
  ordering?: StudentOrdering;
  skip?: boolean;
}): PaginatedResults<Student> {
  const { org } = useOrg();

  const { hasOrgFeature } = useOrgPermissions();

  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  const argument = useMemo(() => {
    if (!context) return null;

    let fs = filterState;

    if (!hasRise) {
      const inCommunityFilter = { inCommunity: YesNoEnum.Yes };
      fs = filterState
        ? filterState.clone({ data: inCommunityFilter })
        : new FilterState(inCommunityFilter);
    }

    return context.buildQueryArgument(
      fs?.getQueryFilter() || undefined,
      ordering?.getOrderingQueryInput() || undefined,
    );
  }, [context, filterState, ordering, hasRise]);

  const { data, loading, error } = useQuery<StudentListQuery>(STUDENT_LIST, {
    variables: {
      ...paginationVariables,
      orgId: org?.id,
      argument: argument,
    },
    skip: skip || !org?.id || !filterState || !context || !argument,
    notifyOnNetworkStatusChange: true,
  });

  return useMemo(() => {
    const studentsResult: PaginatedResults<Student> = {
      items: [],
      hasNextPage: false,
      hasPreviousPage: false,
      totalCount: null,
      loading,
      error,
    };

    const node = data?.node;
    if (node?.__typename === "Organization") {
      const students =
        node.studentList.edges
          ?.map((e) => {
            return e?.node ? processStudent(e?.node) : false;
          })
          .filter((student) => !!student) || [];

      const pageInfo = node.studentList.pageInfo;
      return {
        ...studentsResult,
        ...pageInfo,
        startCursor: pageInfo.startCursor || undefined,
        endCursor: pageInfo.endCursor || undefined,
        totalCount: node.studentList.totalCount,
        items: students,
      };
    }

    return studentsResult;
  }, [data, error, loading]);
}
